/**
 * MillsQuizAPI – fetch product catalog JSON and compute quiz-matched variants (same rules as results-page.js).
 * Depends on quiz-engine.js. Requires a Shopify page at /pages/products-data (template page.products-data).
 */
(function (global) {
  "use strict";

  var cachedProductsPayload = null;
  var cachedProductsPromise = null;

  /** Normalize a tag or type string to the same form used in results CSS classes (spaces → hyphens). */
  function normalizeClassSegment(str) {
    return String(str || "").replace(/\s+/g, "-").trim();
  }

  /** True if product type or any tag matches the segment (same idea as .prod-row .t-* classes). */
  function hasTClass(product, segment) {
    var want = normalizeClassSegment(segment);
    if (!want) return false;
    var pt = normalizeClassSegment(product.productType || "");
    if (pt === want) return true;
    var tags = product.tags || [];
    for (var i = 0; i < tags.length; i++) {
      if (normalizeClassSegment(tags[i]) === want) return true;
    }
    return false;
  }

  /** Same as collection class c-כלבים on the results page (dogs only). */
  function matchesPetCollection(collectionTitles) {
    return (collectionTitles || []).indexOf("כלבים") !== -1;
  }

  /**
   * Merge rows that share the same handle (product in multiple collections) and combine collection titles.
   * Preserves first-seen order.
   */
  function mergeProductsByHandle(rows) {
    var map = {};
    var order = [];
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      var h = r.handle;
      if (!map[h]) {
        order.push(h);
        map[h] = {
          handle: r.handle,
          title: r.title,
          featuredImage: r.featuredImage,
          secondaryImage: r.secondaryImage,
          tags: (r.tags || []).slice(),
          multiplier: r.multiplier,
          productType: r.productType,
          variants: r.variants,
          collectionTitles: r.collectionTitle ? [r.collectionTitle] : [],
          resultDesc: r.resultDesc,
          allIngredients: r.allIngredients,
          supplements: r.supplements,
          nutritional: Array.isArray(r.nutritional) ? r.nutritional.slice() : [],
        };
      } else {
        if (r.collectionTitle && map[h].collectionTitles.indexOf(r.collectionTitle) === -1) {
          map[h].collectionTitles.push(r.collectionTitle);
        }
      }
    }
    var out = [];
    for (var j = 0; j < order.length; j++) {
      out.push(map[order[j]]);
    }
    return out;
  }

  /**
   * Return { variant, variant2 } or null — mirrors findResultVariations in results-page.js.
   */
  function findBestVariantsForProduct(product, calories) {
    var multiplier = parseFloat(String(product.multiplier != null ? product.multiplier : "1"), 10);
    if (isNaN(multiplier) || multiplier <= 0) multiplier = 1;

    var gramsBenchmark = Math.ceil(calories / multiplier);
    var variants = product.variants || [];
    var smartVariantsHigher = [];
    var smartVariantsLower = [];

    for (var k = 0; k < variants.length; k++) {
      var val = variants[k];
      var sku = val.sku;
      if (!sku || String(sku).indexOf("-") === -1) continue;
      var parts = String(sku).split("-");
      var grams = parseFloat(String(parts[1] || "").replace(/[^0-9\.]+/g, ""));
      if (isNaN(grams)) continue;

      var entry = {
        id: val.id,
        available: val.available,
        price: val.price,
        grams: grams,
        gramsBenchmark: gramsBenchmark,
        diff: Math.abs(grams - gramsBenchmark),
        qtyTitle: val.option2,
        name: val.name,
      };
      if (grams >= gramsBenchmark) smartVariantsHigher.push(entry);
      else smartVariantsLower.push(entry);
    }

    smartVariantsHigher.sort(function (a, b) { return a.diff - b.diff; });
    smartVariantsLower.sort(function (a, b) { return a.diff - b.diff; });

    var v1;
    var v2;
    var added = false;
    if (smartVariantsHigher.length > 0) {
      var isAvailable = smartVariantsHigher.length === 1
        ? smartVariantsHigher[0].available
        : smartVariantsHigher[0].available || smartVariantsHigher[1].available;
      if (isAvailable) {
        v1 = smartVariantsHigher[0];
        v2 = smartVariantsHigher[1];
        added = true;
      }
    }
    if (!added && smartVariantsLower.length > 0) {
      var isAvailLow = smartVariantsLower.length === 1
        ? smartVariantsLower[0].available
        : smartVariantsLower[0].available || smartVariantsLower[1].available;
      if (isAvailLow) {
        v1 = smartVariantsLower[0];
        v2 = smartVariantsLower[1];
      }
    }

    if (!v1) return null;

    var variant2 = null;
    if (v2 && v2.available && v2.grams === v1.grams) {
      variant2 = { id: v2.id, price: v2.price, qtyTitle: v2.qtyTitle };
    }

    return {
      variant: {
        id: v1.id,
        price: v1.price,
        grams: v1.grams,
        qtyTitle: v1.qtyTitle,
        name: v1.name,
      },
      variant2: variant2,
    };
  }

  /** Same filter rules as runResultsLogic (results-page.js). */
  function productPassesQuizFilters(product, parsed, V, E, filterOpts) {
    filterOpts = filterOpts || {};
    var weight = parseInt(String(parsed.weight || "").trim(), 10);
    var age = parseInt(String(parsed.age || "").trim(), 10);

    if (!matchesPetCollection(product.collectionTitles)) return false;

    if (weight <= 4 && !hasTClass(product, "מיקרו-גרגיר")) return false;
    if (weight > 7 && hasTClass(product, "מיקרו-גרגיר")) return false;
    if (weight < 10 && hasTClass(product, "סופר-פוד")) return false;
    if (age < 10 && hasTClass(product, "מבוגרים")) return false;

    if (hasTClass(product, "גורים")) return false;
    if (hasTClass(product, "טעימות")) return false;
    if (hasTClass(product, "חטיפים")) return false;
    if (hasTClass(product, "טיפוח")) return false;
    if (hasTClass(product, "אביזרים")) return false;

    var sensitivitiesRaw = parsed.sensitivities === V.YES_EXISTS
      ? (parsed.sensitivities_desc || "") : "";
    var sensitivitiesArr = E.splitCsvTrim(sensitivitiesRaw);
    for (var s = 0; s < sensitivitiesArr.length; s++) {
      if (hasTClass(product, sensitivitiesArr[s])) return false;
    }

    if (!filterOpts.accountPicker) {
      var proteinRaw = parsed.protein === V.YES_EXISTS
        ? (parsed.protein_desc || "") : "";
      var proteinArr = E.splitCsvTrim(proteinRaw);

      if (proteinArr.length > 0) {
        var hasMatchingProtein = false;
        for (var p = 0; p < proteinArr.length; p++) {
          if (hasTClass(product, proteinArr[p])) hasMatchingProtein = true;
        }
        if (!hasMatchingProtein) return false;
      } else {
        if (!hasTClass(product, "best-seller")) return false;
      }
    }

    return true;
  }

  function normalizeQuizParams(quizParams) {
    var parsed = {};
    if (quizParams && typeof quizParams === "object") {
      var keys = Object.keys(quizParams);
      for (var i = 0; i < keys.length; i++) {
        parsed[keys[i]] = quizParams[keys[i]];
      }
    }
    parsed.weight = parseInt(String(parsed.weight || "").trim(), 10);
    parsed.age = parseInt(String(parsed.age || "").trim(), 10);
    return parsed;
  }

  function fetchProductsJson(url) {
    return fetch(url, { credentials: "same-origin" }).then(function (res) {
      if (!res.ok) {
        var hint =
          res.status === 404
            ? " (צרו עמוד עם handle products-data ותבנית page.products-data, או הטמיעו #mills-quiz-products-data בדף)"
            : "";
        throw new Error("[MillsQuizAPI] Failed to load products data: " + res.status + hint);
      }
      return res.json();
    });
  }

  /** Account / themes: JSON embedded in <script type="application/json" id="mills-quiz-products-data"> */
  function tryParseEmbeddedProductsData() {
    if (!global.document) return null;
    var el = global.document.getElementById("mills-quiz-products-data");
    if (!el) return null;
    var raw = (el.textContent || "").trim();
    if (!raw) return null;
    try {
      var data = JSON.parse(raw);
      if (data && Array.isArray(data.products)) return data;
    } catch (e) {
      if (global.console && console.warn) {
        console.warn("[MillsQuizAPI] mills-quiz-products-data is not valid JSON", e);
      }
    }
    return null;
  }

  function loadProductsData(url) {
    if (cachedProductsPayload) {
      return Promise.resolve(cachedProductsPayload);
    }
    if (cachedProductsPromise) {
      return cachedProductsPromise;
    }
    var embedded = tryParseEmbeddedProductsData();
    if (embedded) {
      cachedProductsPayload = embedded;
      cachedProductsPromise = Promise.resolve(embedded);
      return cachedProductsPromise;
    }
    cachedProductsPromise = fetchProductsJson(url).then(
      function (data) {
        cachedProductsPayload = data;
        return data;
      },
      function (err) {
        cachedProductsPromise = null;
        return Promise.reject(err);
      }
    );
    return cachedProductsPromise;
  }

  /**
   * Compute recommended variants for all products that match the quiz (same logic as results page).
   * @param {Object} quizParams — same shape as saveQuizData / loadQuizData
   * @param {Object} [options]
   * @param {string} [options.productsDataUrl] — override default /pages/products-data
   * @param {boolean} [options.clearCache] — force refetch catalog JSON
   * @param {boolean} [options.accountPicker] — skip protein / best-seller filter (account dog picker)
   * @param {boolean} [options.includeProductVariants] — include full variants[] + multiplier on each product row
   * @returns {Promise<{ calories: number, noRecommendations: boolean, products: Array }>}
   */
  function getRecommendations(quizParams, options) {
    options = options || {};
    var E = global.MillsQuizEngine;
    var V = E.VALUES;

    if (!E || typeof E.computeCalories !== "function") {
      return Promise.reject(new Error("[MillsQuizAPI] MillsQuizEngine not loaded"));
    }

    if (options.clearCache) {
      cachedProductsPayload = null;
      cachedProductsPromise = null;
    }

    var base = global.MillsQuizAPI || {};
    var url = options.productsDataUrl || base.productsDataUrl || "/pages/products-data";

    var parsed = normalizeQuizParams(quizParams);
    var calories = E.computeCalories(parsed);

    return loadProductsData(url).then(function (data) {
      var rows = (data && data.products) || [];
      var merged = mergeProductsByHandle(rows);
      var productsOut = [];

      if (calories > E.CALORIES_NO_REC_MAX) {
        return {
          calories: calories,
          noRecommendations: true,
          products: [],
        };
      }

      var filterOpts = { accountPicker: !!options.accountPicker };

      for (var i = 0; i < merged.length; i++) {
        var product = merged[i];
        if (!productPassesQuizFilters(product, parsed, V, E, filterOpts)) continue;
        var picked = findBestVariantsForProduct(product, calories);
        if (!picked) continue;

        var row = {
          handle: product.handle,
          title: product.title,
          featuredImage: product.featuredImage,
          secondaryImage: product.secondaryImage,
          variant: picked.variant,
          variant2: picked.variant2,
          isFirst: productsOut.length === 0,
        };
        if (options.includeProductVariants) {
          row.variants = (product.variants || []).slice();
          row.multiplier = product.multiplier;
          row.resultDesc = product.resultDesc;
          row.allIngredients = product.allIngredients;
          row.supplements = product.supplements;
          row.nutritional = Array.isArray(product.nutritional) ? product.nutritional.slice() : [];
        }
        productsOut.push(row);
      }

      return {
        calories: calories,
        noRecommendations: productsOut.length === 0,
        products: productsOut,
      };
    });
  }

  /** Clear cached JSON from /pages/products-data (e.g. after catalog changes). */
  function clearProductsCache() {
    cachedProductsPayload = null;
    cachedProductsPromise = null;
  }

  global.MillsQuizAPI = {
    getRecommendations: getRecommendations,
    clearProductsCache: clearProductsCache,
    productsDataUrl: "/pages/products-data",
  };
})(typeof window !== "undefined" ? window : this);
